/**
 * ============================================================
 * KEJJA OS — src/pages/calendar/MonthView.tsx
 * Classic 6-week month grid with colored dots per event.
 * ============================================================
 */

import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Repeat } from 'lucide-react';
import { useCalendarContext } from './CalendarLayout';
import { useEvents } from '@/hooks/useCalendar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MonthView() {
  const {
    homeId, cursorDate, setCursorDate,
    visibleCalendarIds, routinesOn, openNewEvent,
  } = useCalendarContext();

  // Compute month range + leading/trailing days
  const { gridDays, monthLabel, fromStr, toStr } = useMemo(() => {
    const year = cursorDate.getUTCFullYear();
    const month = cursorDate.getUTCMonth();

    const firstOfMonth = new Date(Date.UTC(year, month, 1));
    const dowOfFirst = firstOfMonth.getUTCDay();
    const startDow = dowOfFirst === 0 ? 6 : dowOfFirst - 1;    // 0=Mon..6=Sun
    const gridStart = new Date(firstOfMonth);
    gridStart.setUTCDate(firstOfMonth.getUTCDate() - startDow);

    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setUTCDate(gridStart.getUTCDate() + i);
      days.push(d);
    }

    return {
      gridDays: days,
      monthLabel: firstOfMonth.toLocaleDateString('en-KE', { month: 'long', year: 'numeric' }),
      fromStr: days[0]!.toISOString().slice(0, 10),
      toStr: days[41]!.toISOString().slice(0, 10),
    };
  }, [cursorDate]);

  const { data: eventsData, isLoading } = useEvents(
    homeId,
    visibleCalendarIds.size > 0
      ? {
          from: fromStr, to: toStr,
          calendarIds: Array.from(visibleCalendarIds),
          includeRoutines: routinesOn,
        }
      : null,
  );

  const eventsByDate = useMemo(() => {
    const map = new Map<string, typeof eventsData extends { events: infer E } ? E : never>();
    for (const e of eventsData?.events ?? []) {
      const arr = (map.get(e.occurrenceDate) ?? []) as typeof e[];
      arr.push(e);
      map.set(e.occurrenceDate, arr as never);
    }
    return map as Map<string, Array<NonNullable<typeof eventsData>['events'][number]>>;
  }, [eventsData]);

  const routinesByDate = useMemo(() => {
    const map = new Map<string, Array<NonNullable<typeof eventsData>['routines'][number]>>();
    for (const r of eventsData?.routines ?? []) {
      const arr = map.get(r.occurrenceDate) ?? [];
      arr.push(r);
      map.set(r.occurrenceDate, arr);
    }
    return map;
  }, [eventsData]);

  const prevMonth = () => {
    const d = new Date(cursorDate);
    d.setUTCMonth(d.getUTCMonth() - 1);
    setCursorDate(d);
  };
  const nextMonth = () => {
    const d = new Date(cursorDate);
    d.setUTCMonth(d.getUTCMonth() + 1);
    setCursorDate(d);
  };
  const today = () => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    setCursorDate(d);
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const currentMonth = cursorDate.getUTCMonth();

  return (
    <div className="space-y-4 pb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-semibold min-w-[150px] text-center text-sm">
            {monthLabel}
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-xs ml-1" onClick={today}>
            Today
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {/* Day-of-week header */}
          <div className="grid grid-cols-7 border-b border-border">
            {DOW_LABELS.map((d) => (
              <div key={d} className="p-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {d}
              </div>
            ))}
          </div>

          {/* Grid */}
          {isLoading ? (
            <div className="p-4"><Skeleton className="h-96" /></div>
          ) : (
            <div className="grid grid-cols-7">
              {gridDays.map((day, i) => {
                const dateStr = day.toISOString().slice(0, 10);
                const inMonth = day.getUTCMonth() === currentMonth;
                const isToday = dateStr === todayStr;
                const events = eventsByDate.get(dateStr) ?? [];
                const routines = routinesByDate.get(dateStr) ?? [];
                const hasItems = events.length + routines.length > 0;

                return (
                  <button
                    key={i}
                    onClick={() => openNewEvent({ date: dateStr, allDay: true })}
                    className={cn(
                      'min-h-[80px] border-b border-r border-border p-1.5 text-left transition-colors',
                      (i + 1) % 7 === 0 && 'border-r-0',
                      i >= 35 && 'border-b-0',
                      !inMonth && 'bg-muted/30 opacity-60',
                      inMonth && 'hover:bg-muted/40',
                    )}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className={cn(
                        'text-xs font-semibold tabular-nums',
                        isToday && 'bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center',
                      )}>
                        {day.getUTCDate()}
                      </span>
                      {hasItems && (
                        <span className="text-[9px] text-muted-foreground tabular-nums">
                          {events.length + routines.length}
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {events.slice(0, 3).map((e) => (
                        <div
                          key={e.id + e.occurrenceDate}
                          className="text-[10px] truncate px-1 rounded"
                          style={{
                            backgroundColor: `${e.color ?? e.calendarColor}22`,
                            color: e.color ?? e.calendarColor,
                          }}
                        >
                          {e.isReminder && '🔔 '}
                          {!e.allDay && !e.isReminder && (
                            <span className="tabular-nums opacity-70">
                              {new Date(e.startsAt).toISOString().slice(11, 16)} · 
                            </span>
                          )}
                          {' '}{e.title}
                        </div>
                      ))}
                      {routines.length > 0 && (
                        <div className="text-[10px] flex items-center gap-0.5 text-teal-700 dark:text-teal-400 truncate px-1">
                          <Repeat className="w-2 h-2 flex-shrink-0" />
                          {routines.length > 1 ? `${routines.length} routines` : routines[0]!.title}
                        </div>
                      )}
                      {events.length > 3 && (
                        <div className="text-[9px] text-muted-foreground px-1">
                          +{events.length - 3} more
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
